import { Button } from "@talladega/components";
import LocalImage from "@common/components/LocalImage";
import { NEW_BUYER_PROMO_CODE } from "@common/constants";
import SourceEnum, { sources } from "@common/enums/SourceEnum";
import getPromoCodeIsValid from "@modules/purchase/api/getPromoCodeIsValid";
import {
  CustomerQueryParams,
  VehicleQueryParams,
} from "@modules/query-string/types";
import { CarPlusShield } from "@public/images";
import classNames from "classnames";
import { Trans, useTranslation } from "next-i18next";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useDevice } from "@talladega/hooks";

type Props = {
  promoCodeName?: string;
  buyerInformation?: Pick<
    CustomerQueryParams,
    "first" | "last" | "email" | "province"
  >;
  vehicleDetails?: Pick<
    VehicleQueryParams,
    "vin" | "year" | "make" | "model" | "odometer"
  >;
};

const WarrantyCoverageBanner = ({
  promoCodeName = NEW_BUYER_PROMO_CODE,
  buyerInformation,
  vehicleDetails,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const device = useDevice();
  const [promoCodeExists, setPromoCodeExists] = useState(false);

  useEffect(() => {
    getPromoCodeIsValid(promoCodeName).then((res) => {
      setPromoCodeExists(res.error == null);
    });
  }, [promoCodeName]);

  const generateWarrantyLink = () => {
    const params = {
      ...{ ...buyerInformation },
      ...{ ...vehicleDetails },
      promo: promoCodeName,
      skipToQuoteForm: "true",
      utm_source:
        sources
          .find((s) => s.id === SourceEnum.RightRide.toString())
          ?.name.toLowerCase() ?? "",
      utm_medium: "web",
      utm_campaign: "warranty-banner",
    };

    const urlSearchParams = new URLSearchParams(params);

    return `${
      process.env.NEXT_PUBLIC_AFTERMARKET_WARRANTY_BASE_URL
    }?${urlSearchParams.toString()}`;
  };

  return promoCodeExists ? (
    <div className="flex flex-col sm:flex-row-reverse bg-primary-bold rounded-2xl sm:h-[270px]">
      <div className="flex-1 flex flex-col gap-4 p-7 xl:px-20 text-white justify-center">
        <h3 className="font-semibold text-2xl">
          <Trans
            t={t}
            i18nKey="common:protect_your_vehicle_and_save"
            components={{
              textLight: <span className="text-primary-light" />,
            }}
          />
        </h3>
        <p className="text-md font-medium">
          <Trans
            t={t}
            i18nKey="common:get_your_free_quote_today"
            components={{
              b: <span className="font-semibold" />,
            }}
          />
        </p>
        <Link href={generateWarrantyLink()} target="_blank" className="w-fit">
          <Button
            className="w-full sm:w-fit"
            buttonStyle="primary"
            fill="outline"
            spacing="hug"
          >
            <span className=" text-primary-500">
              {t("common:claim_my_discount")}
            </span>
          </Button>
        </Link>
      </div>
      <div className="flex-1 sm:rounded-r-2xl overflow-hidden sm:relative">
        <LocalImage
          fill
          src={CarPlusShield}
          className="hidden sm:inline-block ml-auto object-contain"
          alt="WarrantyPromoLogo"
        />
        <LocalImage
          src={CarPlusShield}
          className={classNames("visible sm:hidden w-full ml-auto", {
            "": !device?.isMobile,
          })}
          alt="WarrantyPromoLogo"
        />
      </div>
    </div>
  ) : null;
};

export default WarrantyCoverageBanner;
